// =====================================================
// Input Sanitization Utility
// AdminMunicipal v6.0 - Security Enhancement
// Sanitizes and validates user input to prevent XSS and injection attacks
// =====================================================

/**
 * Sanitizes text input by trimming whitespace, limiting length,
 * and removing potentially dangerous characters.
 *
 * @param input - The text to sanitize
 * @param maxLength - Maximum allowed length (default: 500)
 * @returns Sanitized text
 */
export function sanitizeText(input: string, maxLength: number = 500): string {
  if (!input) return '';

  return input
    .slice(0, maxLength)
    // Remove angle brackets to prevent HTML injection
    .replace(/[<>]/g, '')
    // Remove null bytes
    .replace(/\0/g, '');
}

/**
 * Sanitizes text for use in textarea fields (allows more characters but still safe).
 *
 * @param input - The text to sanitize
 * @param maxLength - Maximum allowed length (default: 5000)
 * @returns Sanitized text
 */
export function sanitizeTextarea(input: string, maxLength: number = 5000): string {
  if (!input) return '';

  return input
    .slice(0, maxLength)
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove other dangerous tags
    .replace(/<(iframe|object|embed|link|style|meta)[^>]*>/gi, '')
    // Remove null bytes
    .replace(/\0/g, '');
}

/**
 * Sanitizes phone number input by keeping only valid phone characters.
 *
 * @param input - The phone number to sanitize
 * @returns Sanitized phone number
 */
export function sanitizePhone(input: string): string {
  if (!input) return '';

  // Keep only digits, spaces, dashes, parentheses, plus sign
  return input
    .replace(/[^\d\s\-\+\(\)]/g, '')
    .slice(0, 20);
}

/**
 * Validates phone number format (Mexican phone numbers).
 * Accepts 10 digits (local) or 10-15 digits with country code.
 *
 * @param phone - The phone number to validate
 * @returns true if valid, false otherwise
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return true; // Empty is valid (optional field)

  // Extract only digits
  const digits = phone.replace(/\D/g, '');

  // Mexican phones: 10 digits, or with country code: 12-15 digits
  return digits.length >= 10 && digits.length <= 15;
}

/**
 * Sanitizes and validates a number input within bounds.
 *
 * @param input - The input to parse as number
 * @param min - Minimum allowed value (default: 0)
 * @param max - Maximum allowed value (default: 999999)
 * @returns Validated number within bounds
 */
export function sanitizeNumber(input: string | number, min: number = 0, max: number = 999999): number {
  const num = typeof input === 'string' ? parseInt(input, 10) : input;

  if (isNaN(num)) return min;

  return Math.max(min, Math.min(max, num));
}

/**
 * Sanitizes email input.
 * Note: Actual email validation should be done server-side.
 *
 * @param input - The email to sanitize
 * @returns Sanitized email
 */
export function sanitizeEmail(input: string): string {
  if (!input) return '';

  return input
    .trim()
    .toLowerCase()
    .slice(0, 254) // Max email length per RFC 5321
    .replace(/[<>'"]/g, ''); // Remove dangerous characters
}

/**
 * Validates basic email format.
 * Note: This is a basic check; full validation should be done server-side.
 *
 * @param email - The email to validate
 * @returns true if format appears valid
 */
export function validateEmail(email: string): boolean {
  if (!email) return false;

  // Basic email regex - allows most valid emails
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sanitizes a URL input.
 *
 * @param input - The URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(input: string): string {
  if (!input) return '';

  const trimmed = input.trim();

  // Only allow http(s) URLs
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return '';
  }

  try {
    const url = new URL(trimmed);
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * Escapes HTML special characters for safe display.
 * Use this when displaying user-generated content.
 *
 * @param input - The text to escape
 * @returns HTML-escaped text
 */
export function escapeHtml(input: string): string {
  if (!input) return '';

  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return input.replace(/[&<>"']/g, (char) => htmlEntities[char] || char);
}

/**
 * Strips all HTML tags from input.
 *
 * @param input - The text to strip
 * @returns Text with all HTML tags removed
 */
export function stripHtml(input: string): string {
  if (!input) return '';

  return input.replace(/<[^>]*>/g, '');
}
